<template>
    <div class="comment-section">
        <!-- 评论标题 -->
        <div class="comment-header">
            <h3 class="comment-title">
                <MessageCircleIcon :size="20" />
                评论 ({{ totalComments }})
            </h3>
        </div>

        <!-- 发表评论区域 -->
        <div class="comment-form-section">
            <div v-if="!isLoggedIn" class="login-prompt">
                <p>
                    <UserIcon :size="16" />
                    请先登录后再发表评论
                </p>
                <button @click="openAuthModal" class="login-btn">
                    立即登录
                </button>
            </div>

            <div v-else class="comment-form">
                <div class="user-info">
                    <img :src="currentUser?.avatar_url || defaultAvatar"
                        :alt="currentUser?.display_name || currentUser?.username || '用户头像'" class="user-avatar" />
                    <span class="user-name">
                        {{ currentUser?.display_name || currentUser?.username || '匿名用户' }}
                    </span>
                </div>

                <div class="comment-input-area">
                    <textarea v-model="newComment" placeholder="写下你的想法..." class="comment-input" rows="3"
                        maxlength="100" @keydown.ctrl.enter="submitComment"></textarea>
                    <div class="comment-input-footer">
                        <span class="char-count" :class="{ 'warning': newComment.length > 80 }">
                            {{ newComment.length }}/100
                        </span>
                        <button @click="submitComment" class="submit-btn" :disabled="!newComment.trim() || submitting">
                            <SendIcon :size="16" />
                            {{ submitting ? '发表中...' : '发表评论' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- 评论列表 -->
        <div class="comment-list">
            <!-- 加载状态 -->
            <div v-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>加载评论中...</p>
            </div>

            <!-- 无评论状态 -->
            <div v-else-if="comments.length === 0" class="empty-state">
                <MessageCircleIcon :size="48" />
                <p>还没有评论，来发表第一条评论吧！</p>
            </div>

            <!-- 评论项 -->
            <div v-else>
                <div v-for="comment in comments" :key="comment.id" class="comment-item">
                    <img :src="comment.profiles?.avatar_url || defaultAvatar"
                        :alt="comment.profiles?.display_name || comment.profiles?.username || '用户头像'"
                        class="comment-avatar" />
                    <div class="comment-content">
                        <div class="comment-meta">
                            <span class="comment-author">
                                {{ comment.profiles?.display_name || comment.profiles?.username || '匿名用户' }}
                            </span>
                            <span class="comment-time">
                                {{ formatTime(comment.created_at) }}
                            </span>
                            <button v-if="canDeleteComment(comment)" @click="deleteComment(comment.id)"
                                class="delete-btn" title="删除评论">
                                <TrashIcon :size="14" />
                            </button>
                        </div>
                        <p class="comment-text">{{ comment.content }}</p>
                    </div>
                </div>

                <!-- 分页 -->
                <div v-if="totalPages > 1" class="pagination">
                    <button @click="goToPage(currentPage - 1)" :disabled="currentPage <= 1" class="page-btn">
                        <ChevronLeftIcon :size="16" />
                        上一页
                    </button>

                    <span class="page-info">
                        第 {{ currentPage }} 页，共 {{ totalPages }} 页
                    </span>

                    <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" class="page-btn">
                        下一页
                        <ChevronRightIcon :size="16" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {
    MessageCircleIcon,
    UserIcon,
    SendIcon,
    TrashIcon,
    ChevronLeftIcon,
    ChevronRightIcon
} from 'lucide-vue-next'
import supabaseCommentService from '../services/supabaseCommentService'
import supabaseAuthService from '../services/supabaseAuthService'
import supabase from '../services/supabaseClient'

export default {
    name: 'CommentSection',
    components: {
        MessageCircleIcon,
        UserIcon,
        SendIcon,
        TrashIcon,
        ChevronLeftIcon,
        ChevronRightIcon
    },
    props: {
        articleId: {
            type: [String, Number],
            required: true
        }
    },
    emits: ['openAuth'],
    data() {
        return {
            comments: [],
            totalComments: 0,
            currentPage: 1,
            totalPages: 0,
            pageSize: 10,
            loading: true,
            newComment: '',
            submitting: false,
            isLoggedIn: false,
            currentUser: null,
            defaultAvatar: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9IiNGM0Y0RjYiLz4KPGNpcmNsZSBjeD0iMjAiIGN5PSIxNiIgcj0iNiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNNCAzMkM0IDI2LjQ3NzIgOC40NzcyIDIyIDEzIDIySDI3QzMxLjUyMjggMjIgMzYgMjYuNDc3MiAzNiAzMlYzNkg0VjMyWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K'
        }
    },
    async mounted() {
        await this.checkAuthStatus()
        await this.loadComments()
    },
    methods: {
        async checkAuthStatus() {
            try {
                const session = await supabaseAuthService.getSession()
                this.isLoggedIn = !!session?.user

                if (this.isLoggedIn && session.user) {
                    this.currentUser = session.user

                    // 获取用户profile信息（用户名、头像等）
                    try {
                        const { data: profile } = await supabase
                            .from('profiles')
                            .select('username, display_name, avatar_url')
                            .eq('user_id', session.user.id)
                            .single()

                        if (profile) {
                            this.currentUser = {
                                ...this.currentUser,
                                ...profile
                            }
                        }
                    } catch (profileError) {
                        console.log('获取用户profile失败:', profileError)
                        // 即使获取profile失败，用户仍然可以评论
                    }
                }
            } catch (error) {
                console.error('检查登录状态失败:', error)
                this.isLoggedIn = false
            }
        },

        async loadComments() {
            try {
                this.loading = true
                const result = await supabaseCommentService.getCommentsByArticleId(
                    this.articleId,
                    this.currentPage,
                    this.pageSize
                )

                this.comments = result.comments
                this.totalComments = result.total
                this.totalPages = result.totalPages
            } catch (error) {
                console.error('加载评论失败:', error)
                this.$emit('error', '加载评论失败')
            } finally {
                this.loading = false
            }
        },

        async submitComment() {
            if (!this.newComment.trim() || this.submitting) return

            try {
                this.submitting = true

                const newComment = await supabaseCommentService.createComment(
                    this.articleId,
                    this.newComment
                )

                // 将新评论添加到列表开头
                this.comments.unshift(newComment)
                this.totalComments++

                // 清空输入框
                this.newComment = ''

                this.$emit('success', '评论发表成功')
            } catch (error) {
                console.error('发表评论失败:', error)
                this.$emit('error', error.message || '发表评论失败')
            } finally {
                this.submitting = false
            }
        },

        async deleteComment(commentId) {
            if (!confirm('确定要删除这条评论吗？')) return

            try {
                await supabaseCommentService.deleteComment(commentId)

                // 从列表中移除
                this.comments = this.comments.filter(comment => comment.id !== commentId)
                this.totalComments--

                this.$emit('success', '评论删除成功')
            } catch (error) {
                console.error('删除评论失败:', error)
                this.$emit('error', error.message || '删除评论失败')
            }
        },

        canDeleteComment(comment) {
            if (!this.isLoggedIn) return false

            // 用户可以删除自己的评论
            if (comment.user_id === this.currentUser?.id) return true

            // 管理员可以删除任何评论（这里需要获取用户角色信息）
            // 为简化，这里暂时只允许删除自己的评论
            return false
        },

        async goToPage(page) {
            if (page < 1 || page > this.totalPages) return

            this.currentPage = page
            await this.loadComments()
        },

        openAuthModal() {
            this.$emit('openAuth')
        },

        formatTime(time) {
            const date = new Date(time)
            const now = new Date()
            const diffInSeconds = Math.floor((now - date) / 1000)

            if (diffInSeconds < 60) {
                return '刚刚'
            } else if (diffInSeconds < 3600) {
                return `${Math.floor(diffInSeconds / 60)}分钟前`
            } else if (diffInSeconds < 86400) {
                return `${Math.floor(diffInSeconds / 3600)}小时前`
            } else if (diffInSeconds < 2592000) {
                return `${Math.floor(diffInSeconds / 86400)}天前`
            } else {
                return date.toLocaleDateString('zh-CN')
            }
        }
    }
}
</script>

<style scoped>
.comment-section {
    margin-top: 60px;
    padding: 0 80px 60px;
}

.comment-header {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 2px solid #10b981;
}

.comment-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 1.5rem;
    font-weight: 600;
    color: #10b981;
    margin: 0;
}

/* 登录提示 */
.login-prompt {
    background: #f0fdf4;
    border: 1px solid #10b981;
    border-radius: 8px;
    padding: 30px;
    text-align: center;
    margin-bottom: 40px;
}

.login-prompt p {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 0 0 20px 0;
    color: #10b981;
    font-weight: 500;
}

.login-btn {
    background: #10b981;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
}

.login-btn:hover {
    background: #059669;
}

/* 评论表单 */
.comment-form {
    background: #ffffff;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 24px;
    margin-bottom: 40px;
}

.user-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.user-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #10b981;
}

.user-name {
    font-weight: 500;
    color: #2d3748;
}

.comment-input-area {
    position: relative;
}

.comment-input {
    width: 100%;
    padding: 16px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    line-height: 1.5;
    resize: vertical;
    min-height: 80px;
    background: #ffffff;
}

.comment-input:focus {
    outline: none;
    border-color: #10b981;
}

.comment-input-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 12px;
}

.char-count {
    font-size: 12px;
    color: #666;
}

.char-count.warning {
    color: #e53e3e;
}

.submit-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #10b981;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
}

.submit-btn:hover:not(:disabled) {
    background: #059669;
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* 评论列表 */
.comment-list {
    space-y: 24px;
}

.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 60px 20px;
    color: #10b981;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #d1fae5;
    border-top: 3px solid #10b981;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 60px 20px;
    color: #9ca3af;
}

.comment-item {
    display: flex;
    gap: 16px;
    padding: 20px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    margin-bottom: 16px;
}

.comment-item:hover {
    border-color: #10b981;
}

.comment-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid #10b981;
    flex-shrink: 0;
}

.comment-content {
    flex: 1;
}

.comment-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
}

.comment-author {
    font-weight: 500;
    color: #2d3748;
}

.comment-time {
    font-size: 12px;
    color: #9ca3af;
}

.delete-btn {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: all 0.2s ease;
    margin-left: auto;
}

.delete-btn:hover {
    background: rgba(239, 68, 68, 0.1);
}

.comment-text {
    color: #4a5568;
    line-height: 1.6;
    margin: 0;
    word-wrap: break-word;
}

/* 分页 */
.pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 20px;
    margin-top: 40px;
    padding: 20px;
}

.page-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #ffffff;
    border: 1px solid #10b981;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    color: #10b981;
}

.page-btn:hover:not(:disabled) {
    background: #10b981;
    color: white;
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 14px;
    color: #666;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .comment-section {
        padding: 0 40px 40px;
        margin-top: 40px;
    }

    .comment-form {
        padding: 20px;
    }

    .comment-item {
        padding: 16px;
        gap: 12px;
    }

    .comment-avatar,
    .user-avatar {
        width: 32px;
        height: 32px;
    }

    .comment-input-footer {
        flex-direction: column;
        gap: 12px;
        align-items: stretch;
    }

    .pagination {
        flex-direction: column;
        gap: 12px;
    }
}

@media (max-width: 480px) {
    .comment-section {
        padding: 0 20px 30px;
        margin-top: 30px;
    }

    .comment-title {
        font-size: 1.25rem;
    }

    .comment-form {
        padding: 16px;
    }

    .comment-item {
        padding: 12px;
    }
}
</style>